import React, { useState, useEffect } from 'react';
import { viewExpense } from '../../service/ExpenseApi';
import { queryExpenseCategoryConfig } from '../../service/UtilApi';
import { convertTimestampToDate, areDatesEqual, formatINR } from "../../utils/Utilities";
import '../general/General.css';
import { Loading } from "../../components/shared";
const ExpenseStatement = () => {
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    categoryDescription: '',
    subCategoryDescription: ''
  });

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await queryExpenseCategoryConfig();
        if (response?.categoriesList?.length) {
          setCategories(response.categoriesList);
        } else {
          setCategoriesError('பகுப்புகள் எதுவும் கிடைக்கவில்லை');
        }
      } catch (err) {
        setCategoriesError('பகுப்புகளை ஏற்ற முடியவில்லை');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle change for filters (date/category/subcategory/familyId)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'categoryDescription' && { subCategoryDescription: '' }),
    }));
  };

  // Get subcategories for selected category
  const selectedCategory = categories.find(cat => cat.categoryName === filters.categoryDescription);
  const subcategories = selectedCategory?.subcategories || [];

  // Fetch donations
  const fetchData = async () => {
    const { from, to, categoryDescription, subCategoryDescription } = filters;

    if (!categoryDescription) {
      setError('தயவுசெய்து ஒரு வகையைத் தேர்ந்தெடுக்கவும்');
      return;
    }

    if (!subCategoryDescription) {
      setError('தயவுசெய்து ஒரு துணைப்பிரிவைத் தேர்ந்தெடுக்கவும்');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const queryParams = {
        category: categoryDescription,
        subcategory: subCategoryDescription,
        ...(from && { from }),
        ...(to && { to }),
      };

      const resp = await viewExpense(queryParams);

      const sortedTransactions = (resp.expenses || []).sort((a, b) => {
        // Convert transactionDate to JS Date safely
        const dateA = a.transactionDate.toDate ? a.transactionDate.toDate() : new Date(a.transactionDate._seconds * 1000);
        const dateB = b.transactionDate.toDate ? b.transactionDate.toDate() : new Date(b.transactionDate._seconds * 1000);

        return dateA.getTime() - dateB.getTime(); // ascending order
      });

      setTransactions(sortedTransactions);
    } catch (err) {
      console.error(err);
      setError('பட்டியலை ஏற்ற முடியவில்லை');
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <h2>செலவு விவரங்கள் </h2>
      <br />
      <div className="auth_element">


        <label className="auth_label">
          தொடக்க தேதி:
          <input
            className="auth_input"
            name="from"
            type="date"
            onChange={handleChange}
            value={filters.from}
          />
        </label>

        <label className="auth_label">
          முடிவு தேதி:
          <input
            className="auth_input"
            name="to"
            type="date"
            onChange={handleChange}
            value={filters.to}
          />
        </label>

        <label className="auth_label">
          வகை:
          {loadingCategories ? (
            <Loading />
          ) : categoriesError ? (
            <p style={{ color: 'red' }}>{categoriesError}</p>
          ) : (
            <select
              className="auth_input"
              name="categoryDescription"
              value={filters.categoryDescription}
              onChange={handleChange}
            >
              <option value="">-- ஒரு வகையைத் தேர்ந்தெடுக்கவும் --</option>
              {categories.map(cat => (
                <option key={cat.categoryName} value={cat.categoryName}>
                  {cat.description}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="auth_label">
          துணைப்பிரிவு:
          <select
            className="auth_input"
            name="subCategoryDescription"
            value={filters.subCategoryDescription}
            onChange={handleChange}
            disabled={!filters.categoryDescription}
          >
            <option value="">-- ஒரு துணைப்பிரிவைத் தேர்ந்தெடுக்கவும் --</option>
            {subcategories.map(sub => (
              <option key={sub.name} value={sub.name}>
                {sub.description || sub.name}
              </option>
            ))}
          </select>
        </label>

        <button className="auth_button" onClick={fetchData} disabled={loading}>
          {loading ? 'ஏற்றப்படுகிறது…' : 'பட்டியலை பெறவும்'}
        </button>

        {error && <p style={{ color: 'red' }}>{error}</p>}
      </div>

      {transactions.length > 0 ? (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '90px' }}>பரிவர்த்தனை தேதி</th> {/* Effective Date */}
                <th style={{ width: '20%' }}>வகை</th>   {/* Category */}
                <th>துணைப்பிரிவு</th> {/* Subcategory + optional Transaction Date */}
                <th style={{ width: '100px', whiteSpace: 'nowrap' }}>தொகை</th>  {/* Amount */}
              </tr>
            </thead>
            <tbody>
              {transactions.map((d, idx) => {
                const transactionDate = convertTimestampToDate(d.transactionDate);
                const effectiveDate = convertTimestampToDate(d.effectiveDate);
                const datesDiffer = !areDatesEqual(transactionDate, effectiveDate);
                return (
                  <tr key={idx}>
                    <td style={{ width: '80px', whiteSpace: 'nowrap' }}>{transactionDate.toLocaleDateString()}</td>
                    <td style={{ width: '20%' }}>{d.categoryDescription}</td>
                    <td>
                      {d.subCategoryDescription}
                      {datesDiffer && (
                        <> (கணக்கு தேதி: {effectiveDate.toLocaleDateString()})</>
                      )}
                      {d.externalTransactionReference && (
                        <> குறிப்பு:{d.externalTransactionReference}</>
                      )}
                      {(d.receiptNo || d.onlineReceiptNo) && (
                        <>
                          {d.receiptNo && <> ரசீது எண்: {d.receiptNo}</>}
                          {d.receiptNo && d.onlineReceiptNo && ' | '}
                          {d.onlineReceiptNo && <> இ.ரசீது எண்: {d.onlineReceiptNo}</>}
                        </>
                      )}
                    </td>
                    <td style={{ width: '100px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatINR(d.amount)}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                  மொத்த செலவு:
                </td>
                <td style={{ width: '60px', textAlign: 'right', fontWeight: 'bold' }}>
                  {formatINR(transactions.reduce((sum, d) => sum + (d.amount || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>

        </div>
      ) : (
        !loading && <p>பதிவுகள் எதுவும் இல்லை.</p>
      )}
    </>

  );
};

export default ExpenseStatement;
